// P&L mapping — reuses the Financial Report SG&A rules but splits the
// COGS section into "Purchases only" (for CP1) and "Variable service costs"
// (for CP2).

import type { MatchRule, ReportSection } from "../financial-report/mapping";
import { SGA_SECTION } from "../financial-report/mapping";

export const PNL_PURCHASES_LINE = {
  label: "Purchases",
  matches: [
    { group: "Purchased Goods" },
    { group: "Sourcing Fresh" },
    { group: "Sourcing FMCG" },
    { group: "Warehouse", positions: ["loading unloading"] },
  ] as MatchRule[],

};

export const PNL_VARIABLE_LINES: { label: string; matches: MatchRule[] }[] = [
  {
    label: "Packaging",
    matches: [
      { group: "Packaging" },
      { group: "Warehouse", positions: ["PP Bag"] },
    ],
  },
  {
    label: "Lost and Spoiled Goods",
    matches: [{ group: "Lost and Spoiled Goods" }],
  },
  {
    label: "Customer Refunds",
    matches: [
      {
        group: "Customer Service",
        positions: ["Refund", "Tele Subscription", "SGL commission withdrawal"],
      },
    ],
  },
  {
    label: "Payment Provider Fees",
    matches: [
      { group: "Payment Provider Fees" },
      { group: "Finance", positions: ["Payment Transfer Fees"] },
      { group: "Admin Cost", positions: ["Payment Transfer Fees", "Transfer Fees"] },
    ],
  },
  {
    label: "Delivery Service",
    matches: [
      { group: "Delivery Service" },
      { group: "Admin Cost", positions: ["Fuel"] },
    ],
  },
];

// SG&A reused unchanged from Financial Report.
export const PNL_SGA_SECTION: ReportSection = SGA_SECTION;

// P&L-only SG&A additions (not in the cash-flow Financial Report):
// - Depreciation: emitted synthetically by the P&L hook as a group named
//   "Depreciation" that spreads Asset-category purchases over 60 months (5 years).
export const PNL_SGA_EXTRA_LINES: { label: string; matches: MatchRule[] }[] = [
  {
    label: "Depreciation",
    matches: [{ group: "Depreciation" }],
  },
];

// ── Per-expense classifier ──────────────────────────────────────────────────
// Given a single expense's cost-group + position labels, return which P&L line
// it belongs to (COGS or SG&A), or null when unmapped. Used to reshape a set of
// individual expenses (e.g. one business unit's) into the accrual P&L layout.
//
// Lines are evaluated in report order (Purchases → variable COGS → SG&A) and the
// FIRST matching line wins. Combined with the `excludePositions` rules, this
// reproduces the report's `consumed`-set behaviour without a shared set:
// an earlier line claims a position before a later whole-group/exclude rule can.
// Depreciation is intentionally omitted (no per-expense source).

const norm = (s: string) => s.trim().toLowerCase();

function ruleMatches(rule: MatchRule, g: string, p: string): boolean {
  if (norm(rule.group) !== g) return false;
  if ("positions" in rule) return rule.positions.some((x) => norm(x) === p);
  if ("excludePositions" in rule)
    return !rule.excludePositions.some((x) => norm(x) === p);
  return true; // whole group
}

type ClassifiedLine = { section: "cogs" | "sga"; line: string; matches: MatchRule[] };

const CLASSIFIER_LINES: ClassifiedLine[] = [
  { section: "cogs", line: PNL_PURCHASES_LINE.label, matches: PNL_PURCHASES_LINE.matches },
  ...PNL_VARIABLE_LINES.map((l) => ({ section: "cogs" as const, line: l.label, matches: l.matches })),
  ...PNL_SGA_SECTION.lines.map((l) => ({ section: "sga" as const, line: l.label, matches: l.matches })),
];

/** Ordered COGS line labels (Purchases first), for rendering all lines in order. */
export const PNL_COGS_LINES: string[] = [
  PNL_PURCHASES_LINE.label,
  ...PNL_VARIABLE_LINES.map((l) => l.label),
];

/** Ordered SG&A line labels. */
export const PNL_SGA_LINES: string[] = PNL_SGA_SECTION.lines.map((l) => l.label);

export function classifyExpenseToPnlLine(
  groupLabel: string,
  positionLabel: string
): { section: "cogs" | "sga"; line: string } | null {
  const g = norm(groupLabel);
  const p = norm(positionLabel);
  for (const entry of CLASSIFIER_LINES) {
    if (entry.matches.some((rule) => ruleMatches(rule, g, p))) {
      return { section: entry.section, line: entry.line };
    }
  }
  return null;
}
